/**
 * The ONE way a check reads the questions on a CRF form (2026-10-19).
 *
 * Several verification families each carried their own copy of "the questions
 * on a form", and one of them (the protocol-fact checks, W1-W14) read
 * `form.sections` where loadCrfFormsForProject puts the questions under
 * `form.content.sections`. Every loader-shaped form therefore looked empty to
 * it, and the Verification page filled with false warnings ("no field for
 * sex", "the SAE form misses all six criteria"). Measured on NFP-HTN-302 and
 * KBL-UC-204 on Clinical Dev.
 *
 * Shapes this reads, in this order of precedence (the first that yields
 * questions wins, so a form is never read twice):
 *   1. a loaded CrfForm: `content.sections[].questions` then the deprecated
 *      `content.columns[]` (content may be an object or a JSON string);
 *   2. bare content: `sections[].questions` then `columns[]` at the top level
 *      (the shape the protocol-fact fixtures and some callers pass);
 *   3. a CRF Builder session form: `variables[]` with varName / questionText /
 *      responseType / options {code,label}, mapped to the stored question
 *      shape exactly as crf/loadCrfForms.ts sessionFormToContent maps it.
 *
 * PURE and browser-safe. Absent or malformed input is an empty list, never a
 * throw: an empty list is what lets a caller SKIP on absent input rather than
 * pass on it.
 */
import type { CrfQuestion } from "./crfTypes";

export interface CrfQuestionInSection {
  question: CrfQuestion;
  /** The section title ("" when the section has none). Absent for legacy columns and session variables. */
  section?: string;
  /** Where the question came from: a section, the deprecated `columns[]`, or a session form's `variables[]`. */
  from: "section" | "columns" | "variables";
}

const arr = (x: unknown): any[] => (Array.isArray(x) ? x : []);

function parsed(content: unknown): any {
  if (typeof content === "string") {
    try { return JSON.parse(content); } catch { return null; }
  }
  return content && typeof content === "object" ? content : null;
}

function fromSectionsAndColumns(c: any): CrfQuestionInSection[] {
  const out: CrfQuestionInSection[] = [];
  if (!c || typeof c !== "object") return out;
  for (const s of arr(c.sections)) {
    if (!s || typeof s !== "object") continue;
    const title = typeof s.title === "string" ? s.title : "";
    for (const q of arr(s.questions)) if (q && typeof q === "object") out.push({ question: q, section: title, from: "section" });
  }
  for (const q of arr(c.columns)) if (q && typeof q === "object") out.push({ question: q, from: "columns" });
  return out;
}

/** A CRF Builder session variable as a stored question (mirrors sessionFormToContent). */
function fromSessionVariable(v: any, idx: number): CrfQuestion {
  return {
    ...(v && typeof v === "object" ? v : {}),
    id: typeof v?.id === "string" ? v.id : `var-${idx + 1}`,
    version: typeof v?.version === "number" ? v.version : 1,
    number: idx + 1,
    text: typeof v?.questionText === "string" ? v.questionText : typeof v?.text === "string" ? v.text : "",
    variable: typeof v?.varName === "string" ? v.varName : typeof v?.variable === "string" ? v.variable : "",
    response_type: (v?.responseType ?? v?.response_type ?? "text") as CrfQuestion["response_type"],
    options: arr(v?.options)
      .filter((o) => (o && typeof o === "object") || typeof o === "string")
      .map((o: any) => (typeof o === "string" ? { label: o, value: o } : { label: o.label ?? o.text, value: o.code ?? o.value, ...(typeof o.definition === "string" && o.definition ? { definition: o.definition } : {}) })),
  } as CrfQuestion;
}

/** Every question on a form, with the title of the section it sits in. */
export function crfQuestionsWithSection(form: unknown): CrfQuestionInSection[] {
  const f: any = form && typeof form === "object" ? form : null;
  if (!f) return [];
  const fromContent = fromSectionsAndColumns(parsed(f.content));
  if (fromContent.length) return fromContent;
  const bare = fromSectionsAndColumns(f);
  if (bare.length) return bare;
  return arr(f.variables).filter((v) => v && typeof v === "object").map((v, i) => ({ question: fromSessionVariable(v, i), from: "variables" as const }));
}

/** Every question on a form, in the order the form presents them. */
export function crfQuestionsOf(form: unknown): CrfQuestion[] {
  return crfQuestionsWithSection(form).map((x) => x.question);
}
